// simulateBotReply — a keyword-matched stand-in for the AI service (OpenAI/RAG lives in the Python
// backend). It is deliberately simple but genuinely driven by the bot's saved configuration, so
// Bot Configuration, Bot Management and the visitor widget all behave as one system:
//   - bot.greeting / config.fallbackMessage / config.handoff / config.prohibitedTopics are honoured,
//   - config.responseLength caps the reply,
//   - an explicit request for a person escalates immediately (handoff.triggers.explicitRequest).
// It does NOT read the Knowledge Base (that wiring belongs to the real RAG pipeline).

const HUMAN_RE = /\b(human|real person|live agent|an agent|representative|operator|(?:talk|speak|chat)\s+(?:to|with)\s+(?:a\s+|an\s+)?(?:someone|person|agent))\b/i;
const NAME_SET_RE = /\bmy\s+name\s+is\s+([a-z][a-z .'-]{1,50})$/i;
const NAME_ASK_RE = /(what is|what's|whats|do you know)\s+(my\s+)?name\b|\bremember\s+my\s+name\b/i;
const LENGTH_CAPS = { short: 120, balanced: 300, detailed: 600 };

const topicText = (topic) => String(topic).toLowerCase().replace(/[-_]+/g, " ").trim();

export function isHumanRequest(message) {
  return HUMAN_RE.test(message || "");
}

export function simulateBotReply(bot, conversation, message) {
  const config = bot?.config || {};
  const handoff = config.handoff || {};
  const text = String(message || "").trim();
  const lower = text.toLowerCase();
  const result = { reply: "", matched: true, escalate: false, reason: null, visitorName: null, truncated: false };

  const nameMatch = text.match(NAME_SET_RE);
  if (nameMatch) {
    result.visitorName = nameMatch[1].trim().replace(/[.]+$/, "");
    result.reply = `Nice to meet you, ${result.visitorName}! I’ll remember your name for this active session.`;
    return finish(result, config);
  }
  if (NAME_ASK_RE.test(lower)) {
    result.reply = conversation?.visitorName
      ? `Your name is ${conversation.visitorName}.`
      : "I don't have your name yet. Please tell me: My name is <your name>.";
    return finish(result, config);
  }

  const banned = [...(config.prohibitedTopics || []), ...(config.customProhibitedTopics || [])].map(topicText).filter(Boolean);
  if (banned.some((topic) => lower.includes(topic))) {
    result.matched = false;
    result.reason = "prohibited-topic";
    result.reply = config.responseBoundaryMessage || config.fallbackMessage || "I'm not able to help with that topic.";
    return finish(result, config);
  }

  const keywordHit = (handoff.keywords || []).some((keyword) => keyword && lower.includes(String(keyword).toLowerCase()));
  const explicit = isHumanRequest(text) && handoff.triggers?.explicitRequest !== false;
  if (handoff.enabled !== false && (explicit || (handoff.triggers?.keywordMatch && keywordHit))) {
    result.escalate = true;
    result.reason = explicit ? "explicit-request" : "keyword";
    result.reply = handoff.message || "I'm connecting you with a human agent now — they'll join this chat shortly.";
    return finish(result, config);
  }
  if (isHumanRequest(text) && handoff.enabled === false) {
    result.matched = false;
    result.reply = "Human handoff isn't available right now, but I'll do my best to help here.";
    return finish(result, config);
  }

  if (/\b(hi|hello|hey)\b/.test(lower)) result.reply = bot?.greeting || "Hello! 👋 How can I help?";
  else if (/pricing|price|cost|plan/.test(lower)) result.reply = "Sure! I can help with pricing. Please select a plan or tell me what you need, and I’ll guide you through the available options.";
  else if (/demo|book/.test(lower)) result.reply = "Absolutely! I can help you book a demo. Please share your preferred date and time, and our team can follow up.";
  else if (/support|issue|problem|error|login|password/.test(lower)) result.reply = "Sure — I can help with technical support. Please describe the issue you are facing, including any error message you see.";
  else if (/hour|open|time/.test(lower)) result.reply = "Check our Business Profile for exact business hours — outside those hours your message is queued.";
  else if (/refund|cancel/.test(lower)) result.reply = "Refunds follow our published policy. Want me to connect you with support for the specifics?";
  else {
    result.matched = false;
    result.reply = config.fallbackMessage || "I don't have enough information to answer that confidently. Would you like me to connect you with a human agent?";
  }
  return finish(result, config);
}

function finish(result, config) {
  const cap = LENGTH_CAPS[config.responseLength] || LENGTH_CAPS.balanced;
  if (result.reply.length > cap) {
    result.reply = `${result.reply.slice(0, Math.max(0, cap - 1))}…`;
    result.truncated = true;
  }
  return { ...result, tone: config.tone, responseStyle: config.responseStyle };
}

/** Rough intent label for a lead, derived from what the visitor actually asked about. */
export function detectIntent(messages) {
  const text = (messages || []).filter((m) => m.from === "visitor").map((m) => m.text).join(" ").toLowerCase();
  if (/demo|book/.test(text)) return "Book a demo";
  if (/pricing|price|cost|plan|quote/.test(text)) return "Pricing enquiry";
  if (/support|issue|problem|error|login|password/.test(text)) return "Support";
  return "Visitor chat enquiry";
}
